import React, { useCallback, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Flex,
  Spacer,
  VStack,
  HStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
  Switch,
  Text as ChakraText,
} from '@chakra-ui/react'
import { colors } from '../../../../../ui-kit'

import type { InputFormProps as Props } from '../../../../interfaces'
import { getFieldsMapping } from '../../../../../utils/mediaLibrary'
import OperatorMenu from '../../OperatorMenu'

const CustomMetadataBooleanInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    customMetadataField,
    validationObject,
    groupId,
    filterId,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const booleanMapping = useMemo(
    () => ({
      true: true,
      false: false,
    }),
    [],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({
          fieldName: expression.fieldName,
          customMetadataField,
        }).getInitialValue(newOperator)
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em">
        <Flex alignItems="stretch" width="full" flexDirection="row">
          <OperatorMenu
            width="45%"
            expression={expression}
            customMetadataField={customMetadataField}
            handleOperatorChange={handleOperatorChange}
          />

          <Spacer />

          {['IS'].includes(expression.operator) &&
            !Array.isArray(expression.value) && (
              <HStack alignItems="center" minWidth="45%" maxWidth="45%">
                <Switch
                  name={expression.fieldName}
                  defaultIsChecked
                  isChecked={booleanMapping[expression.value as string]}
                  onChange={e => {
                    handleValueChange(String(e.target.checked))
                    setDirty(true)
                  }}
                />
                <ChakraText color={colors.appSlate} fontWeight="medium">
                  {expression.value}
                </ChakraText>
              </HStack>
            )}
        </Flex>
        <VStack width="full" alignItems="end">
          <ChakraFormErrorMessage maxWidth="45%">
            {error}
          </ChakraFormErrorMessage>
        </VStack>
      </VStack>
    </ChakraFormControl>
  )
}

export default CustomMetadataBooleanInputForm
